// mobile_v1: responsive — guide stack แนวตั้ง + form 1 column + cards
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Send, Plus, Trash2, Edit2, Save, X, Power, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import UsageBanner from '../components/UsageBanner'; // telegram_usage_banner_v1
// telegram_path_to_manuals_v1 — applied 2026-05-13T13-08-46

const emptyForm = { name: '', bot_token: '', chat_id: '', is_active: true };


// channel_telegram_routing_ui_v1: per-bot channel subscription manager
function SubscribedChannels({ telegramId, API, toast }) {
  const [channels, setChannels] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (expanded && channels === null) {
      API.get(`/api/telegrams/${telegramId}/channels`)
        .then(r => setChannels(r.data))
        .catch(() => setChannels([]));
    }
  }, [expanded, telegramId, API, channels]);

  const toggle = (chId) => {
    setChannels(prev => prev.map(c =>
      c.id === chId ? { ...c, is_assigned: !c.is_assigned } : c
    ));
  };

  const save = async () => {
    if (!channels) return;
    setSaving(true);
    try {
      const ids = channels.filter(c => c.is_assigned).map(c => c.id);
      await API.put(`/api/telegrams/${telegramId}/channels`, { channel_ids: ids });
      if (toast) toast.success('💾 บันทึกแล้ว');
    } catch (err) {
      if (toast) toast.error('บันทึกไม่สำเร็จ: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const assignedCount = channels ? channels.filter(c => c.is_assigned).length : 0;

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#0ea5e9', fontWeight: 600, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
        {expanded ? '▼' : '▶'} 📡 ช่องที่ส่งแจ้งเตือนไปบอทนี้
        {channels && <span style={{ background:'#e0f2fe', color:'#0369a1', borderRadius:6, padding:'1px 8px', fontSize:11 }}>
          {assignedCount} / {channels.length}
        </span>}
      </button>
      {expanded && (
        <div style={{ marginTop: 10, paddingLeft: 16 }}>
          {channels === null && <div style={{ color: '#94a3b8', fontSize: 13 }}>กำลังโหลด...</div>}
          {channels && channels.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>ยังไม่มีช่องในระบบ</div>
          )}
          {channels && channels.length > 0 && (
            <>
              <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>
                💡 ถ้าไม่เลือกช่องไหนเลย = บอทนี้จะรับการแจ้งเตือนทุกช่องที่ไม่ได้ assign ให้บอทอื่น
              </div>
              <div style={{ display:'grid', gap:6, maxHeight:300, overflow:'auto', marginBottom:10 }}>
                {channels.map(ch => (
                  <label key={ch.id} style={{
                    display:'flex', alignItems:'center', gap:8,
                    padding:'6px 10px', borderRadius:6,
                    background: ch.is_assigned ? '#f0fdf4' : '#f8fafc',
                    cursor:'pointer',
                    fontSize:13,
                  }}>
                    <input
                      type="checkbox"
                      checked={!!ch.is_assigned}
                      onChange={() => toggle(ch.id)}
                      style={{ cursor:'pointer' }}
                    />
                    <span style={{ fontWeight: 600, color:'#1e293b' }}>{ch.name}</span>
                    {ch.tag && <span style={{ background:'#f59e0b20', color:'#92400e', borderRadius:6, padding:'1px 6px', fontSize:11 }}>📁 {ch.tag}</span>}
                    {ch.telegram_id && ch.telegram_id !== telegramId && (
                      <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:6, padding:'1px 6px', fontSize:10 }}>
                        ⚠️ assigned to อื่น
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <button
                onClick={save}
                disabled={saving}
                style={{
                  background:'#0ea5e9', color:'#fff', border:'none',
                  borderRadius:6, padding:'6px 14px', fontWeight:600, fontSize:13,
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}>
                {saving ? 'กำลังบันทึก...' : '💾 บันทึก'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TelegramSettings() {
  const [telegrams, setTelegrams] = useState([]);
  // telegram_help_modal_v1: full guide modal state (unique name to avoid conflict)
  const [guideModal, setGuideModal] = useState(false);
  useEffect(() => {
    if (!guideModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setGuideModal(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [guideModal]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = async () => {
    const { data } = await API.get('/api/telegrams');
    setTelegrams(data);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.bot_token || !form.chat_id) {
      toast.error('กรุณากรอกข้อมูลให้ครบ'); return;
    }
    setLoading(true);
    try {
      if (editId) {
        await API.put(`/api/telegrams/${editId}`, form);
        toast.success('อัปเดตสำเร็จ');
      } else {
        await API.post('/api/telegrams', form);
        toast.success('เพิ่ม Telegram สำเร็จ! ส่งข้อความทดสอบแล้ว');
      }
      setForm(emptyForm); setEditId(null); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setLoading(false);
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      await API.post(`/api/telegrams/${id}/test`);
      toast.success('ส่งข้อความทดสอบสำเร็จ!');
    } catch (err) { toast.error(err.response?.data?.error || 'ส่งไม่สำเร็จ'); }
    setTesting(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบ Telegram นี้?')) return;
    await API.delete(`/api/telegrams/${id}`);
    toast.success('ลบแล้ว'); load();
  };

  const handleToggle = async (tg) => {
    await API.put(`/api/telegrams/${tg.id}`, { ...tg, is_active: !tg.is_active });
    load();
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background: '#f8fafc', minHeight: '100%' }}>
      <UsageBanner resource="telegrams" /> {/* telegram_usage_banner_v1 */}
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Send size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b' }}>Telegram แจ้งเตือน</h1>
            <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#64748b' }}>จัดการ Telegram Bot สำหรับแจ้งเตือน</p>
          </div>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8,
            padding: isMobile ? '12px 18px' : '10px 18px',
            fontSize: isMobile ? 14 : 13, fontWeight: 600, cursor: 'pointer',
          }}>
          <Plus size={isMobile ? 16 : 15} />เพิ่ม Telegram
        </button>
      </div>

      {/* How to guide — collapsible บน mobile */}
      <div style={{
        background: '#0f172a',
        borderRadius: 12,
        padding: isMobile ? 14 : 20,
        marginBottom: isMobile ? 16 : 24,
        color: '#94a3b8',
        fontSize: isMobile ? 12 : 13,
      }}>
              {/* telegram_help_modal_v1: prominent guide button */}
      <button
        onClick={() => setGuideModal(true)}
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: 14,
          padding: isMobile ? '18px 20px' : '22px 28px',
          marginBottom: 16,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          boxShadow: '0 8px 24px rgba(14,165,233,0.25)',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(14,165,233,0.35)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(14,165,233,0.25)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, textAlign: 'left' }}>
          <div style={{
            width: isMobile ? 44 : 52,
            height: isMobile ? 44 : 52,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? 24 : 28,
            flexShrink: 0,
          }}>📖</div>
          <div>
            <div style={{
              fontSize: isMobile ? 16 : 18,
              fontWeight: 700,
              marginBottom: 2,
            }}>คู่มือเชื่อมต่อ Telegram Bot</div>
            <div style={{
              fontSize: isMobile ? 12 : 13,
              opacity: 0.92,
              fontWeight: 400,
            }}>สอนทีละขั้นตอน — สร้างบอท เก็บ Token หา Chat ID และผูกกับระบบ</div>
          </div>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.18)',
          borderRadius: 8,
          padding: isMobile ? '8px 14px' : '10px 18px',
          fontSize: isMobile ? 13 : 14,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>เปิดดู →</div>
      </button>

      {/* telegram_help_modal_v1: modal overlay */}
      {guideModal && (
        <div
          onClick={() => setGuideModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,20,25,0.75)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 10 : 30,
            backdropFilter: 'blur(4px)',
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#f7f3eb',
              width: '100%',
              maxWidth: 1000,
              height: '95vh',
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '12px 16px' : '14px 22px',
              background: '#fff',
              borderBottom: '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>📖</span>
                <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#0f1419' }}>คู่มือเชื่อมต่อ Telegram Bot</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <a
                  href="/manuals/telegram-setup.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                  title="เปิดในแท็บใหม่"
                >↗ แท็บใหม่</a>
                <button
                  onClick={() => setGuideModal(false)}
                  style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  title="ปิด (Esc)"
                >✕ ปิด</button>
              </div>
            </div>
            <iframe
              src="/manuals/telegram-setup.html"
              style={{
                flex: 1,
                width: '100%',
                border: 'none',
                background: '#f7f3eb',
              }}
              title="คู่มือ Telegram Bot Setup"
            />
          </div>
        </div>
      )}

      <div
          onClick={isMobile ? () => setShowGuide(!showGuide) : undefined}
          style={{
            fontWeight: 600, color: '#f1f5f9',
            marginBottom: isMobile && !showGuide ? 0 : 8,
            cursor: isMobile ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
          <span>📋 วิธีเพิ่ม Telegram Bot</span>
          {isMobile && (
            <span style={{ fontSize: 18, transform: showGuide ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
          )}
        </div>
        {(!isMobile || showGuide) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 16 : 12,
          }}>
            <div>
              <p style={{ color: '#0ea5e9', fontWeight: 600, marginBottom: 4, marginTop: 0 }}>Bot Token:</p>
              <p style={{ margin: '2px 0' }}>1. ไปที่ @BotFather ใน Telegram</p>
              <p style={{ margin: '2px 0' }}>2. พิมพ์ /newbot แล้วตั้งชื่อ</p>
              <p style={{ margin: '2px 0' }}>3. Copy Token ที่ได้</p>
            </div>
            <div>
              <p style={{ color: '#0ea5e9', fontWeight: 600, marginBottom: 4, marginTop: 0 }}>Chat ID:</p>
              <p style={{ margin: '2px 0' }}>1. เพิ่ม Bot เข้า Group/Channel</p>
              <p style={{ margin: '2px 0' }}>2. ส่งข้อความใน Group</p>
              <p style={{ margin: '2px 0', wordBreak: 'break-all' }}>3. เปิด: api.telegram.org/bot[TOKEN]/getUpdates</p>
              <p style={{ margin: '2px 0' }}>4. หา "chat":{"{"}"id": ...</p>
            </div>
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{
          background: '#fff', borderRadius: 14,
          padding: isMobile ? 16 : 28,
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
          marginBottom: isMobile ? 16 : 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: isMobile ? 14 : 15, fontWeight: 600, color: '#1e293b' }}>
              {editId ? '✏️ แก้ไข Telegram' : '➕ เพิ่ม Telegram ใหม่'}
            </h2>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={20} color="#94a3b8" />
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr',
            gap: isMobile ? 12 : 14,
            marginBottom: isMobile ? 16 : 20,
          }}>
            <FormField label="ชื่อ (เพื่อระบุ)" isMobile={isMobile}>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="เช่น ทีมขาย, ทีม Support"
                style={inputStyle(isMobile)} />
            </FormField>
            <FormField label="Bot Token" isMobile={isMobile}>
              <input value={form.bot_token} onChange={e => setForm({ ...form, bot_token: e.target.value })}
                placeholder="1234567890:ABCdefGHI..."
                style={inputStyle(isMobile)} />
            </FormField>
            <FormField label="Chat ID" isMobile={isMobile}>
              <input value={form.chat_id} onChange={e => setForm({ ...form, chat_id: e.target.value })}
                placeholder="-100123456789"
                style={inputStyle(isMobile)} />
            </FormField>
          </div>
          <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{
                padding: isMobile ? '12px 20px' : '9px 20px',
                borderRadius: 8, background: '#f1f5f9',
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                order: isMobile ? 2 : 1,
              }}>ยกเลิก</button>
            <button onClick={handleSave} disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: isMobile ? '12px 24px' : '9px 24px',
                borderRadius: 8, background: '#0ea5e9', color: '#fff',
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 600,
                order: isMobile ? 1 : 2,
              }}>
              <Save size={14} />{loading ? 'กำลังบันทึก...' : 'บันทึกและทดสอบ'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {telegrams.length === 0 && !showForm ? (
        <div style={{
          background: '#fff', borderRadius: 14,
          padding: 48, textAlign: 'center', color: '#94a3b8',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        }}>
          <Send size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>ยังไม่มี Telegram Bot<br/>กดปุ่ม "เพิ่ม Telegram" เพื่อเริ่มต้น</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: isMobile ? 10 : 12 }}>
          {telegrams.map(tg => (
            <div key={tg.id} style={{
              background: '#fff', borderRadius: 12,
              padding: isMobile ? 14 : '18px 24px',
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              borderLeft: `4px solid ${tg.is_active ? '#22c55e' : '#e2e8f0'}`,
            }}>
              {/* Top row: ชื่อ + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: isMobile ? 16 : 15 }}>{tg.name}</span>
                <span style={{
                  background: tg.is_active ? '#dcfce7' : '#f1f5f9',
                  color: tg.is_active ? '#16a34a' : '#94a3b8',
                  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                }}>
                  {tg.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Token + Chat ID */}
              <div style={{
                fontSize: 12, color: '#94a3b8',
                marginBottom: isMobile ? 12 : 0,
                wordBreak: 'break-all',
              }}>
                Token: {tg.bot_token.substring(0, isMobile ? 12 : 15)}... {!isMobile && '|'} {isMobile && <br />}
                Chat ID: {tg.chat_id}
              </div>

              {/* Actions — Wrap บน mobile */}
              <div style={{
                display: 'flex',
                gap: 8,
                marginTop: isMobile ? 0 : -28,
                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}>
                <button onClick={() => handleTest(tg.id)} disabled={testing === tg.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: '#f0f9ff', color: '#0ea5e9', border: 'none',
                    borderRadius: 8,
                    padding: isMobile ? '8px 14px' : '7px 14px',
                    cursor: 'pointer', fontSize: isMobile ? 13 : 12, fontWeight: 600,
                  }}>
                  <Send size={13} />{testing === tg.id ? 'ส่ง...' : 'ทดสอบ'}
                </button>
                <button onClick={() => { setEditId(tg.id); setForm({ name: tg.name, bot_token: tg.bot_token, chat_id: tg.chat_id, is_active: tg.is_active }); setShowForm(true); }}
                  style={iconBtn('#f1f5f9', isMobile)}>
                  <Edit2 size={14} color="#64748b" />
                </button>
                <button onClick={() => handleToggle(tg)}
                  style={iconBtn(tg.is_active ? '#fee2e2' : '#dcfce7', isMobile)}>
                  <Power size={14} color={tg.is_active ? '#dc2626' : '#16a34a'} />
                </button>
                <button onClick={() => handleDelete(tg.id)}
                  style={iconBtn('#fff1f2', isMobile)}>
                  <Trash2 size={14} color="#ef4444" />
                </button>
              </div>
              <SubscribedChannels telegramId={tg.id} API={API} toast={toast} /> {/* channel_telegram_routing_ui_v1 */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FormField = ({ label, isMobile, children }) => (
  <div>
    <label style={{
      fontSize: 12, fontWeight: 600, color: '#64748b',
      display: 'block', marginBottom: 4,
    }}>{label}</label>
    {children}
  </div>
);

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});

const iconBtn = (bg, isMobile) => ({
  background: bg, border: 'none', borderRadius: 8,
  padding: isMobile ? '8px 12px' : '7px 10px',
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minWidth: isMobile ? 40 : 'auto',
  minHeight: isMobile ? 40 : 'auto',
});
